// ── Portal access: two facts, one reading ───────────────────────────────────
//
// There are two separate things recorded about a contact and the client portal,
// and they answer different questions:
//
//   portalaccess    a hand-set legacy bit — somebody decided this person
//                   SHOULD have access. It comes from a platform where the
//                   portal and the loan data lived in different systems and
//                   neither could check the other, so it was the only record
//                   there was.
//   portalloginid   derived, from dbo.customerUser — this person HAS a login.
//
// THEY ARE NOT THE SAME FACT, and today they disagree for most of the book:
// the flag runs well ahead of the logins because provisioning has not caught
// up. Deriving the flag from the login would have silently revoked the tick
// for everybody in that gap — no error, the tick just stops being there.
//
// So both are kept and the DISAGREEMENT is the useful part. "Pending setup" is
// the provisioning worklist, and it empties itself as people get set up. When
// it reaches zero the legacy column has nothing left to say and can be retired
// on evidence rather than on hope.
//
// This is the ONLY place that reading lives. The contact list, the account page
// and the contact form all call it, so they cannot drift into three slightly
// different opinions about who has access.

// Funded and active. The same two strings dbo.vInvoiceNoticeCandidates tests,
// so a borrower cannot be live enough to email but too closed for the portal.
// Non Accrual counts as live on purpose: troubled is not finished.
const SERVICING = ["Accrual", "Non Accrual"];

export const portalState = c => {
  const flagged = !!c.portalaccess;
  const status = String(c.portalloginstatus || "").toLowerCase();
  const hasLogin = c.portalloginid != null;
  // Absent accountstatus means the caller did not ask for it. Treat that as
  // servicing rather than closed — a missing field must never invent an
  // offboarding, and the states below then behave exactly as they did before.
  const closed = c.accountstatus != null && !SERVICING.includes(c.accountstatus);

  // Checked before anything else: an open door at a borrower who has left is
  // the one state here with a security cost, so it outranks every other
  // reading of the same row.
  if (hasLogin && status === "active" && closed) {
    // Same distinction as below: a login on an unfunded deal is early, not
    // stale, and telling somebody to deactivate it would undo deliberate work.
    if (c.accountstatus === "Funding in Progress") {
      return {
        key: "active",
        label: "Portal login",
        bg: "#E1F5EE", fg: "#0F6E56",
        note: "Set up ahead of funding — the account has not funded yet.",
      };
    }
    return {
      key: "offboard",
      label: "Offboard — account closed",
      bg: "#FBE4DD", fg: "#993C1D",
      note: `Can still sign in, but this borrower is ${c.accountstatus}. Deactivate the login in Client Portal Login.`,
    };
  }

  if (hasLogin && status === "active") {
    return {
      key: "active",
      label: "Portal login",
      bg: "#E1F5EE", fg: "#0F6E56",
      // Whether they have ever used it is a different question from whether
      // they can, and it is the one nobody thinks to ask.
      note: c.portallastlogin ? null : "Set up, but has never signed in.",
    };
  }

  if (hasLogin) {
    // A disabled login cannot sign in. A tick claiming otherwise is a lie in
    // the opposite direction, so this reads as no access whatever the flag
    // says — the flag does not get to override the door being locked.
    return {
      key: "disabled",
      label: "Login disabled",
      bg: "#F1EFE8", fg: "#888780",
      note: "Set to inactive in Client Portal Login — cannot sign in.",
    };
  }

  // A flag saying "set this person up" at a borrower that paid off is not a
  // task, it is a leftover from when they were a client. Kept OUT of the
  // worklist: a to-do list that asks you to provision people at closed
  // accounts trains you to ignore the to-do list.
  //
  // Checked before the cross-account case below, because at a closed borrower
  // the cross-account question no longer needs answering.
  if (flagged && closed) {
    // 'Funding in Progress' is NOT a closed relationship — it is one that has
    // not started. dbo.vAccountStatus returns it for any account with no
    // funded terms, which is every prospect in the pipeline, so this is the
    // common case rather than the rare one. "Clear the flag" would be exactly
    // the wrong instruction for a deal that funds in a fortnight.
    if (c.accountstatus === "Funding in Progress") {
      return {
        key: "prefunding",
        label: "Not funded yet",
        bg: "#F1EFE8", fg: "#888780",
        note: "Flagged for access, but the account has not funded. Set them up once it does — nothing to do yet.",
      };
    }
    return {
      key: "stale",
      label: "Stale flag",
      bg: "#F1EFE8", fg: "#888780",
      note: `Flagged for access, but this borrower is ${c.accountstatus}. Nothing to set up — the flag can be cleared.`,
    };
  }

  // One person, two borrowers, one login. dbo.customerUser carries
  // UQ_customerUser_email — unique on email ALONE, not on (email, account) —
  // so a person who already has a login somewhere cannot be given a second one
  // here. This is NOT a provisioning task; it is a thing the schema currently
  // forbids, and it needs a junction table before it can be done at all.
  //
  // Kept separate from "pending setup" deliberately. A worklist that contains
  // an item nobody can action is a worklist people stop trusting.
  if (flagged && c.portalloginotheraccount) {
    return {
      key: "blocked",
      label: "Blocked — login elsewhere",
      bg: "#FBE4DD", fg: "#993C1D",
      note: `Already has a portal login on ${c.portalloginotheraccount}. The portal allows one login per email address, so they cannot also be set up here.`,
    };
  }

  if (flagged) {
    return {
      key: "pending",
      label: "Pending setup",
      bg: "#FDF0D5", fg: "#7A5200",
      note: c.email
        ? "Flagged for access, but no portal login exists for this address."
        : "Flagged for access, but this contact has no email address to set one up with.",
    };
  }

  return { key: "none", label: "—", bg: null, fg: null, note: null };
};

export const portalPending = c => portalState(c).key === "pending";

// ── The same relationship, read from the portal end ─────────────────────────
//
// portalState answers "does this CONTACT have a login". This answers "does
// this LOGIN have a contact". Same pair of rows, opposite direction, and they
// live in the same file on purpose: two files would drift, and the two screens
// would then disagree about the same person.
//
// A login with no contact is NOT broken — they can sign in today. It means they
// are invisible to everything built on Contacts: the invoice notice recipient
// list, the contact list, the account page. Someone reading their statements
// who does not appear anywhere you would look for them.
export const contactState = u => {
  if (u.contactid != null) {
    return { key: "linked", label: "Contact", bg: "#E1F5EE", fg: "#0F6E56",
             note: u.contactname || null };
  }

  if (u.contactelsewhere) {
    // Not missing — misfiled. The fix is to move the contact record or the
    // login, not to create a second copy of the person.
    return { key: "otheraccount", label: "Contact on another account",
             bg: "#FDF0D5", fg: "#7A5200",
             note: `Signs in here, but their contact record is on ${u.contactelsewhere}. One of the two is on the wrong account.` };
  }

  return { key: "none", label: "No contact record", bg: "#FBE4DD", fg: "#993C1D",
           note: "Can sign in, but nobody holds this address in Contacts for this borrower — so they are invisible to invoice notices and the contact list." };
};

export const ContactBadge = ({ user }) => {
  const s = contactState(user);
  return (
    <span title={s.note || s.label}
          style={{ fontSize: 11.5, background: s.bg, color: s.fg, padding: "2px 8px",
                   borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>
      {s.key === "linked" && s.note ? s.note : s.label}
    </span>
  );
};

// The badge carries its own explanation in the tooltip. A colour alone tells
// you something is off without telling you what to do about it.
export const PortalBadge = ({ contact }) => {
  const s = portalState(contact);
  if (!s.bg) return <span style={{ color: "var(--color-text-secondary)" }}>—</span>;
  return (
    <span title={s.note || s.label}
          style={{ fontSize: 11.5, background: s.bg, color: s.fg, padding: "2px 8px",
                   borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
};
